import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, logout as logoutApi } from '../api/auth';
import { getAccounts, getBalance, getPortfolioHistory, createSnapshot, manualSell, getTodayOrders, getTradeStats, getTradeHistory, getHoldingsChart } from '../api/account';
import { usePortfolioWebSocket } from '../hooks/usePortfolioWebSocket';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import Sidebar from '../components/Sidebar';
import StockChartCard from '../components/StockChartCard';
import CollectingOverlay from '../components/CollectingOverlay';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [restBalance, setRestBalance] = useState(null);
  
  // 차트 관련 상태
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartType, setChartType] = useState('total_value'); // total_value, profit_rate, profit
  const [chartDays, setChartDays] = useState(30);
  const [customDateRange, setCustomDateRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [todayOrders, setTodayOrders] = useState([]);
  const [tradeStats, setTradeStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'stats', 'chart', 'history', 'holdingsChart'
  const [tradeHistory, setTradeHistory] = useState([]);
  const [tradeHistoryLoading, setTradeHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(0);
  const [holdingsChartData, setHoldingsChartData] = useState([]);
  const [holdingsChartLoading, setHoldingsChartLoading] = useState(false);
  
  const { 
    balance: wsBalance, 
    connected, 
    lastUpdate, 
    refresh 
  } = usePortfolioWebSocket(user?.id);
  
  const balance = wsBalance || restBalance;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userData = await getMe();
        setUser(userData);
        
        const accountsData = await getAccounts();
        setAccounts(accountsData.accounts);
        
        if (accountsData.accounts.length > 0) {
          setSelectedAccount(accountsData.accounts[0]);
        }
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);


  const handleSell = async (stock) => {
    if (!window.confirm(`${stock.stock_name} ${stock.quantity}주를 시장가로 청산하시겠습니까?`)) {
      return;
    }
    try {
      await manualSell(selectedAccount.id, stock.stock_code, stock.quantity);
      alert("청산 주문이 완료되었습니다.");
      // 체결 반영 대기 후 페이지 새로고침
      await new Promise(resolve => setTimeout(resolve, 1000));
      window.location.reload();
    } catch (error) {
      console.error("청산 실패:", error);
      alert(error.response?.data?.detail || "청산 주문에 실패했습니다.");
    }
  };

  useEffect(() => {
    const fetchBalance = async () => {
      if (!selectedAccount) return;
      
      setBalanceLoading(true);
      try {
        const balanceData = await getBalance(selectedAccount.id);
        setRestBalance(balanceData);
      } catch (err) {
        console.error('잔고 조회 실패:', err);
        setRestBalance(null);
      } finally {
        setBalanceLoading(false);
      }
    };
    fetchBalance();
  }, [selectedAccount]);

  // 거래 통계 로드
  useEffect(() => {
    const fetchTradeStats = async () => {
      if (!selectedAccount) return;
      try {
        const data = await getTradeStats(selectedAccount.id);
        setTradeStats(data);
      } catch (err) {
        console.error('거래 통계 로드 실패:', err);
        setTradeStats(null);
      }
    };
    fetchTradeStats();
  }, [selectedAccount]);

  // 차트 데이터 로드
  useEffect(() => {
    const fetchChartData = async () => {
      if (!selectedAccount) return;
      
      setChartLoading(true);
      try {
        // 직접선택 모드면 날짜 범위로, 아니면 days로 조회
        const data = customDateRange && startDate && endDate
          ? await getPortfolioHistory(selectedAccount.id, 30, startDate, endDate)
          : await getPortfolioHistory(selectedAccount.id, chartDays);
        setChartData(data.history || []);
      } catch (err) {
        console.error('차트 데이터 로드 실패:', err);
        setChartData([]);
      } finally {
        setChartLoading(false);
      }
    };
    fetchChartData();
  }, [selectedAccount, chartDays, customDateRange, startDate, endDate]);

  // 당일 거래내역 로드
  useEffect(() => {
    const fetchTodayOrders = async () => {
      if (!selectedAccount) return;
      try {
        const data = await getTodayOrders(selectedAccount.id);
        setTodayOrders(data.orders || []);
      } catch (err) {
        console.error('당일 거래내역 로드 실패:', err);
        setTodayOrders([]);
      }
    };
    fetchTodayOrders();
  }, [selectedAccount]);

  // 거래내역(청산 내역) 로드
  useEffect(() => {
    const fetchTradeHistory = async () => {
      if (!selectedAccount || activeTab !== 'history') return;
      setTradeHistoryLoading(true);
      try {
        const data = await getTradeHistory(selectedAccount.id, historyPage, 20);
        setTradeHistory(data.trades || []);
        setHistoryTotalPages(data.total_pages || 0);
      } catch (err) {
        console.error('거래내역 로드 실패:', err);
        setTradeHistory([]);
      } finally {
        setTradeHistoryLoading(false);
      }
    };
    fetchTradeHistory();
  }, [selectedAccount, activeTab, historyPage]);

  // 보유종목 차트 데이터 로드 (종목 구성이 바뀔 때만 재로딩)
  const holdingsKey = balance?.holdings?.map(h => h.stock_code).sort().join(',') || '';
  useEffect(() => {
    const fetchHoldingsChart = async () => {
      if (!selectedAccount || activeTab !== 'holdingsChart') return;
      if (!holdingsKey) return;

      setHoldingsChartLoading(true);
      try {
        const stockCodes = holdingsKey.split(',');
        const data = await getHoldingsChart(selectedAccount.id, 200, stockCodes);
        setHoldingsChartData(data.holdings || []);
      } catch (err) {
        console.error('보유종목 차트 로드 실패:', err);
        setHoldingsChartData([]);
      } finally {
        setHoldingsChartLoading(false);
      }
    };
    fetchHoldingsChart();
  }, [selectedAccount, activeTab, holdingsKey]);

  const handleLogout = () => {
    logoutApi();
    navigate('/login');
  };

  const handleRefresh = async () => {
    if (connected) {
      refresh();
    } else if (selectedAccount) {
      setBalanceLoading(true);
      try {
        const balanceData = await getBalance(selectedAccount.id);
        setRestBalance(balanceData);
      } catch (err) {
        console.error('잔고 조회 실패:', err);
      } finally {
        setBalanceLoading(false);
      }
    }
  };

  const handleCreateSnapshot = async () => {
    if (!selectedAccount) return;
    try {
      await createSnapshot(selectedAccount.id);
      // 차트 데이터 새로고침
      const data = await getPortfolioHistory(selectedAccount.id, chartDays);
      setChartData(data.history || []);
      alert('스냅샷이 저장되었습니다.');
    } catch (err) {
      alert('스냅샷 저장 실패');
    }
  };

  const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return amount.toLocaleString('ko-KR') + '원';
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return date.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const formatChartValue = (value) => {
    if (chartType === 'profit_rate') {
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
    }
    return `${value >= 0 ? '+' : ''}${value.toLocaleString()}원`;
  };

  const getChartDataKey = () => {
    switch (chartType) {
      case 'profit_rate': return 'profit_rate';
      case 'profit': return 'profit';
      default: return 'total_value';
    }
  };

  const getChartColor = () => {
    if (chartData.length < 2) return '#6366f1';
    const first = chartData[0][getChartDataKey()];
    const last = chartData[chartData.length - 1][getChartDataKey()];
    return last >= first ? '#ef4444' : '#3b82f6';
  };

  // Y축 domain 계산 (총자산은 데이터 범위 기준, 수익금/수익률은 0 중심)
  const getYAxisDomain = () => {
    if (chartData.length === 0) return ['auto', 'auto'];

    const dataKey = getChartDataKey();
    const values = chartData.map(d => d[dataKey]).filter(v => v != null);
    if (values.length === 0) return ['auto', 'auto'];

    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const range = maxVal - minVal;
    const padding = range * 0.1 || (maxVal * 0.05); // 10% 패딩, range가 0이면 5%

    if (chartType === 'total_value') {
      // 총자산: 데이터 범위 기준으로 패딩 추가
      return [Math.floor(minVal - padding), Math.ceil(maxVal + padding)];
    } else {
      // 수익금/수익률: 0을 포함하도록 설정
      const absMax = Math.max(Math.abs(minVal), Math.abs(maxVal));
      return [Math.floor(-absMax - padding), Math.ceil(absMax + padding)];
    }
  };

  if (loading) {
    return (
      <CollectingOverlay>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </CollectingOverlay>
    );
  }

  return (
    <CollectingOverlay>
    <div className="min-h-screen bg-gray-50">
      <Sidebar user={user} onLogout={handleLogout} />

      <main className="p-4 md:p-8 pt-20 md:pt-24 w-full max-w-6xl mx-auto">
        <header className="flex justify-between items-center mb-3 md:mb-4">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-base md:text-2xl font-bold text-gray-800">대시보드</h1>
              <p className="text-xs text-gray-500">내 투자 현황을 확인하세요</p>
            </div>
            <div className="flex space-x-1 ml-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-2 py-1 text-xs font-medium rounded transition ${
                  activeTab === 'overview'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                현황
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`px-2 py-1 text-xs font-medium rounded transition ${
                  activeTab === 'stats'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                통계
              </button>
              <button
                onClick={() => setActiveTab('chart')}
                className={`px-2 py-1 text-xs font-medium rounded transition ${
                  activeTab === 'chart'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                수익곡선
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-2 py-1 text-xs font-medium rounded transition ${
                  activeTab === 'history'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                거래내역
              </button>
              <button
                onClick={() => setActiveTab('holdingsChart')}
                className={`px-2 py-1 text-xs font-medium rounded transition ${
                  activeTab === 'holdingsChart'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                차트
              </button>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm">
              {(() => {
                const now = new Date();
                const day = now.getDay();
                const hours = now.getHours();
                const minutes = now.getMinutes();
                const time = hours * 60 + minutes;
                const isWeekday = day >= 1 && day <= 5;
                const isMarketHours = time >= 9 * 60 && time <= 15 * 60 + 30;
                const isMarketOpen = isWeekday && isMarketHours;
                
                if (isMarketOpen && connected) {
                  return (
                    <>
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-green-600 text-xs">실시간</span>
                    </>
                  );
                } else if (isWeekday && !isMarketHours) {
                  return (
                    <>
                      <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                      <span className="text-gray-500 text-xs">장 마감</span>
                    </>
                  );
                } else {
                  return (
                    <>
                      <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                      <span className="text-gray-500 text-xs">휴장</span>
                    </>
                  );
                }
              })()}
            </div>
            
            {accounts.length > 0 && (
              <select
                value={selectedAccount?.id || ''}
                onChange={(e) => {
                  const account = accounts.find(a => a.id === e.target.value);
                  setSelectedAccount(account);
                }}
                className="px-2 py-1 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none"
              >
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.account_no}
                  </option>
                ))}
              </select>
            )}
          </div>
        </header>

        {accounts.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <div className="text-6xl mb-4">🔗</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">연결된 계좌가 없습니다</h2>
            <p className="text-gray-500 mb-6">한국투자증권 계좌를 연결하여 자동매매를 시작하세요</p>
            <button
              onClick={() => navigate('/account')}
              className="px-6 py-3 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition"
            >
              계좌 연결하기
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {/* 계좌 요약 - 메인 4대 지표 (한 줄 구성) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">총평가금액</span>
                <span className="text-sm font-bold text-gray-800 tabular-nums">{formatMoney(balance?.total_balance)}</span>
              </div>
              <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">총자산</span>
                <span className="text-sm font-bold text-gray-800 tabular-nums">{formatMoney((balance?.total_balance || 0) - (balance?.profit_loss || 0))}</span>
              </div>
              <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">평가손익</span>
                <span className={`text-sm font-bold tabular-nums ${(balance?.profit_loss || 0) >= 0 ? 'text-red-500' : 'text-blue-500'}`}>
                  {(balance?.profit_loss || 0) >= 0 ? '+' : ''}{formatMoney(balance?.profit_loss)}
                </span>
              </div>
              <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">수익률</span>
                <span className={`text-sm font-bold tabular-nums ${(() => {
                    const total = balance?.total_balance || 0;
                    const yesterdayCapital = balance?.yesterday_base_capital;
                    if (!yesterdayCapital) return 'text-gray-400';
                    return total >= yesterdayCapital ? 'text-red-500' : 'text-blue-500';
                  })()}`}>
                  {(() => {
                    const total = balance?.total_balance || 0;
                    const yesterdayCapital = balance?.yesterday_base_capital;
                    if (!yesterdayCapital) return '-';
                    const profit = total - yesterdayCapital;
                    const rate = (profit / yesterdayCapital) * 100;
                    return `${rate >= 0 ? '+' : ''}${rate.toFixed(2)}%`;
                  })()}
                </span>
              </div>
            </div>

            {/* 추가 정보 4개 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">누적수익률</span>
                <span className={`text-sm font-bold tabular-nums ${(() => {
                    const total = balance?.total_balance || 0;
                    const startCapital = balance?.start_base_capital;
                    if (!startCapital) return 'text-gray-400';
                    return total >= startCapital ? 'text-red-500' : 'text-blue-500';
                  })()}`}>
                  {(() => {
                    const total = balance?.total_balance || 0;
                    const startCapital = balance?.start_base_capital;
                    if (!startCapital) return '-';
                    const profit = total - startCapital;
                    const rate = (profit / startCapital) * 100;
                    return `${rate >= 0 ? '+' : ''}${rate.toFixed(2)}%`;
                  })()}
                </span>
              </div>
              <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">누적수익금</span>
                <span className={`text-sm font-bold tabular-nums ${(() => {
                    const total = balance?.total_balance || 0;
                    const startCapital = balance?.start_base_capital;
                    if (!startCapital) return 'text-gray-400';
                    return total >= startCapital ? 'text-red-500' : 'text-blue-500';
                  })()}`}>
                  {(() => {
                    const total = balance?.total_balance || 0;
                    const startCapital = balance?.start_base_capital;
                    if (!startCapital) return '-';
                    const profit = total - startCapital;
                    return `${profit >= 0 ? '+' : ''}${formatMoney(profit)}`;
                  })()}
                </span>
              </div>
              <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">주식평가</span>
                <span className="text-sm font-bold text-gray-800 tabular-nums">{formatMoney(balance?.stock_balance || 0)}</span>
              </div>
              <div className="bg-white rounded-lg shadow-sm px-3 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-500 font-medium">보유종목</span>
                <span className="text-sm font-bold text-gray-800">{balance?.holdings?.length || 0}종목</span>
              </div>
            </div>

            {/* 현황 탭 */}
            {activeTab === 'overview' && (
            <>
            {/* 보유 종목 */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <h3 className="text-sm text-gray-500 font-medium">보유 종목</h3>
                  <span className="text-sm bg-gray-100 px-2 py-1 rounded-full text-gray-600">
                    {balance?.holdings?.length || 0}개
                  </span>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={balanceLoading}
                  className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  {balanceLoading ? '로딩...' : '새로고침'}
                </button>
              </div>
              
              {balanceLoading && !balance ? (
                <div className="text-center py-8 text-gray-400">로딩 중...</div>
              ) : balance?.holdings?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="table-fixed w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="w-[17%] text-left py-1.5 px-2 text-xs font-medium text-gray-500">종목</th>
                        <th className="w-[9%] text-right py-1.5 px-2 text-xs font-medium text-gray-500">현재가</th>
                        <th className="w-[10%] hidden md:table-cell text-right py-1.5 px-2 text-xs font-medium text-gray-500">평균단가</th>
                        <th className="w-[7%] hidden md:table-cell text-right py-1.5 px-2 text-xs font-medium text-gray-500">수량</th>
                        <th className="w-[14%] hidden md:table-cell text-right py-1.5 px-2 text-xs font-medium text-gray-500">평가금액</th>
                        <th className="w-[14%] hidden md:table-cell text-right py-1.5 px-2 text-xs font-medium text-gray-500">손익</th>
                        <th className="w-[9%] text-right py-1.5 px-2 text-xs font-medium text-gray-500">수익률</th>
                        <th className="w-[7%] hidden md:table-cell text-center py-1.5 px-1 text-xs font-medium text-gray-500">보유</th>
                        <th className="w-[5%] text-center py-1.5 px-1 text-xs font-medium text-gray-500"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {balance.holdings.map((stock, idx) => (
                        <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-1.5 px-2">
                            <p className="font-medium text-gray-800 text-xs truncate">{stock.stock_name}</p>
                            <p className="text-xs text-gray-500 hidden md:block">{stock.stock_code}</p>
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums font-medium text-gray-800 text-xs">
                            {stock.current_price?.toLocaleString()}
                          </td>
                          <td className="hidden md:table-cell py-1.5 px-2 text-right tabular-nums text-gray-600 text-xs">
                            {stock.avg_price?.toLocaleString()}
                          </td>
                          <td className="hidden md:table-cell py-1.5 px-2 text-right tabular-nums text-gray-600 text-xs">
                            {stock.quantity}
                          </td>
                          <td className="hidden md:table-cell py-1.5 px-2 text-right tabular-nums text-gray-800 text-xs">
                            {(stock.current_price * stock.quantity)?.toLocaleString()}
                          </td>
                          <td className={`hidden md:table-cell py-1.5 px-2 text-right tabular-nums font-medium text-xs ${
                            stock.profit_loss >= 0 ? 'text-red-500' : 'text-blue-500'
                          }`}>
                            {stock.profit_loss >= 0 ? '+' : ''}
                            {stock.profit_loss?.toLocaleString()}
                          </td>
                          <td className={`py-1.5 px-2 text-right tabular-nums font-medium text-xs ${
                            stock.profit_rate >= 0 ? 'text-red-500' : 'text-blue-500'
                          }`}>
                            {stock.profit_rate >= 0 ? '+' : ''}
                            {stock.profit_rate?.toFixed(2)}%
                          </td>
                          <td className="hidden md:table-cell py-1.5 px-1 text-center tabular-nums text-gray-600 text-xs">
                            {stock.holding_days_display || '-'}
                          </td>
                          <td className="py-1.5 px-1 text-center">
                            <button
                              onClick={() => handleSell(stock)}
                              className="px-1.5 py-0.5 text-xs bg-red-500 hover:bg-red-600 text-white rounded transition-colors"
                            >
                              청산
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-400 text-xs">
                  보유 중인 종목이 없습니다
                </div>
              )}
            </div>

            {/* 당일 거래내역 */}
            <div className="bg-white rounded-xl shadow-sm p-3 mt-2">
              <h3 className="text-sm text-gray-500 font-medium mb-2">당일 거래내역</h3>
              {todayOrders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-1.5 px-2 text-xs font-medium text-gray-500">시간</th>
                        <th className="text-left py-1.5 px-2 text-xs font-medium text-gray-500">종목</th>
                        <th className="text-center py-1.5 px-2 text-xs font-medium text-gray-500">구분</th>
                        <th className="text-right py-1.5 px-2 text-xs font-medium text-gray-500">수량</th>
                        <th className="text-right py-1.5 px-2 text-xs font-medium text-gray-500">단가</th>
                        <th className="text-center py-1.5 px-2 text-xs font-medium text-gray-500">보유일</th>
                        <th className="text-right py-1.5 px-2 text-xs font-medium text-gray-500">손익</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayOrders.map((order) => (
                        <tr key={order.id} className="border-b border-gray-50">
                          <td className="py-1.5 px-2 text-xs text-gray-600 tabular-nums">
                            {order.created_at?.length >= 4 ? `${order.created_at.slice(0,2)}:${order.created_at.slice(2,4)}` : order.created_at}
                          </td>
                          <td className="py-1.5 px-2 text-xs font-medium text-gray-800">{order.stock_name}</td>
                          <td className={`py-1.5 px-2 text-xs text-center font-medium ${order.order_type === 'buy' ? 'text-red-500' : 'text-blue-500'}`}>
                            {order.order_type === 'buy' ? '매수' : '매도'}
                          </td>
                          <td className="py-1.5 px-2 text-xs text-right text-gray-600 tabular-nums">{order.quantity}</td>
                          <td className="py-1.5 px-2 text-xs text-right text-gray-800 tabular-nums">{order.price?.toLocaleString()}</td>
                          <td className="py-1.5 px-2 text-xs text-center text-gray-600 tabular-nums">
                            {order.order_type === 'sell' ? (order.holding_days_display || '-') : '진입'}
                          </td>
                          <td className={`py-1.5 px-2 text-xs text-right font-medium tabular-nums ${
                            order.order_type === 'sell'
                              ? (order.profit_loss >= 0 ? 'text-red-500' : 'text-blue-500')
                              : 'text-gray-400'
                          }`}>
                            {order.order_type === 'sell'
                              ? (
                                <span>
                                  {order.profit_loss >= 0 ? '+' : ''}{order.profit_loss?.toLocaleString()}원
                                  <span className="ml-1 text-[10px]">
                                    ({order.profit_rate >= 0 ? '+' : ''}{order.profit_rate?.toFixed(1)}%)
                                  </span>
                                </span>
                              )
                              : '-'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-2 text-gray-400 text-xs">
                  오늘 거래내역이 없습니다
                </div>
              )}
            </div>
            </>
            )}

            {/* 통계 탭 */}
            {activeTab === 'stats' && (
              <div className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="text-sm text-gray-500 font-medium mb-4">거래 통계</h3>
                {tradeStats && tradeStats.total_trades > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">총 거래</p>
                      <p className="text-lg font-bold">{tradeStats.total_trades}회</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">승률</p>
                      <p className="text-lg font-bold text-red-500">{tradeStats.win_rate}%</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">수익/손실</p>
                      <p className="text-lg font-bold">{tradeStats.win_trades}승 {tradeStats.loss_trades}패</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Profit Factor</p>
                      <p className="text-lg font-bold">{tradeStats.profit_factor}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">평균 손익</p>
                      <p className={`text-lg font-bold ${tradeStats.avg_profit >= 0 ? 'text-red-500' : 'text-blue-500'}`}>
                        {tradeStats.avg_profit >= 0 ? '+' : ''}{tradeStats.avg_profit?.toLocaleString()}원
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">최대 수익</p>
                      <p className={`text-lg font-bold ${tradeStats.max_profit >= 0 ? 'text-red-500' : 'text-blue-500'}`}>
                        {tradeStats.max_profit >= 0 ? '+' : ''}{tradeStats.max_profit?.toLocaleString()}원
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">최대 손실</p>
                      <p className="text-lg font-bold text-blue-500">{tradeStats.max_loss?.toLocaleString()}원</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">최대 연승</p>
                      <p className="text-lg font-bold">{tradeStats.max_win_streak}연승</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    {tradeStats?.stats_start_date ? `${tradeStats.stats_start_date} 이후 거래 없음` : '거래 데이터가 쌓이면 통계가 표시됩니다'}
                  </div>
                )}
              </div>
            )}

            {/* 수익곡선 탭 */}
            {activeTab === 'chart' && (
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm text-gray-500 font-medium">누적 수익 곡선</h3>
                <div className="flex items-center space-x-3">
                  {/* 차트 타입 선택 */}
                  <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setChartType('total_value')}
                      className={`px-1.5 py-0.5 text-xs rounded transition ${
                        chartType === 'total_value'
                          ? 'bg-white text-gray-800 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      총자산
                    </button>
                    <button
                      onClick={() => setChartType('profit')}
                      className={`px-1.5 py-0.5 text-xs rounded transition ${
                        chartType === 'profit'
                          ? 'bg-white text-gray-800 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      수익금
                    </button>
                    <button
                      onClick={() => setChartType('profit_rate')}
                      className={`px-1.5 py-0.5 text-xs rounded transition ${
                        chartType === 'profit_rate'
                          ? 'bg-white text-gray-800 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      수익률
                    </button>
                  </div>

                  {/* 기간 선택 */}
                  <div className="flex items-center gap-1">
                    <select
                      value={customDateRange ? 'custom' : chartDays}
                      onChange={(e) => {
                        if (e.target.value === 'custom') {
                          setCustomDateRange(true);
                        } else {
                          setCustomDateRange(false);
                          setChartDays(Number(e.target.value));
                        }
                      }}
                      className="px-2 py-0.5 text-xs border border-gray-200 rounded-lg focus:outline-none"
                    >
                      <option value={7}>7일</option>
                      <option value={30}>30일</option>
                      <option value={90}>90일</option>
                      <option value={9999}>전체</option>
                      <option value="custom">직접선택</option>
                    </select>
                    {customDateRange && (
                      <div className="flex items-center gap-1">
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none"
                        />
                        <span className="text-xs text-gray-400">~</span>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="px-1 py-0.5 text-xs border border-gray-200 rounded focus:outline-none"
                        />
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {chartLoading ? (
                <div className="h-44 flex items-center justify-center text-gray-400 text-xs">
                  로딩 중...
                </div>
              ) : chartData.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                        tickFormatter={(value) => value.slice(5)}
                      />
                      <YAxis
                        yAxisId="left"
                        domain={getYAxisDomain()}
                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                        tickFormatter={(value) => {
                          if (chartType === 'profit_rate') return `${value}%`;
                          if (Math.abs(value) >= 100000000) return `${(value / 100000000).toFixed(1)}억`;
                          if (Math.abs(value) >= 10000) return `${(value / 10000).toFixed(0)}만`;
                          return value;
                        }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                        tickFormatter={(value) => {
                          if (Math.abs(value) >= 10000) return `${(value / 10000).toFixed(0)}만`;
                          return value;
                        }}
                      />
                      <Tooltip
                        formatter={(value, name) => {
                          const formatted = chartType === 'profit_rate' ? `${value}%` : `${value?.toLocaleString()}원`;
                          const label = name === 'daily_change' ? '일별손익' :
                            chartType === 'total_value' ? '총자산' :
                            chartType === 'profit' ? '누적수익' : '수익률';
                          return [formatted, label];
                        }}
                        labelFormatter={(label) => label}
                      />
                      <ReferenceLine yAxisId="right" y={0} stroke="#d1d5db" strokeDasharray="3 3" />
                      <Bar
                        yAxisId="right"
                        dataKey="daily_change"
                        fill="#94a3b8"
                        opacity={0.5}
                        radius={[2, 2, 0, 0]}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey={getChartDataKey()}
                        stroke={getChartColor()}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-44 flex items-center justify-center text-gray-400 text-xs">
                  <div className="text-center">
                    <p>기록된 데이터가 없습니다</p>
                    <p className="text-sm mt-1">매일 19:00에 자동 저장됩니다</p>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* 거래내역 탭 */}
            {activeTab === 'history' && (
              <div className="bg-white rounded-xl shadow-sm p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm text-gray-500 font-medium">거래내역</h3>
                  {historyTotalPages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                        disabled={historyPage === 1}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        이전
                      </button>
                      <span className="text-xs text-gray-500">{historyPage} / {historyTotalPages}</span>
                      <button
                        onClick={() => setHistoryPage(p => Math.min(historyTotalPages, p + 1))}
                        disabled={historyPage === historyTotalPages}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        다음
                      </button>
                    </div>
                  )}
                </div>
                {tradeHistoryLoading ? (
                  <div className="text-center py-8 text-gray-400 text-xs">로딩 중...</div>
                ) : tradeHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">거래일</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">종목명</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">매수가</th>
                          <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">매수일</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">편출가</th>
                          <th className="text-center py-2 px-2 text-xs font-medium text-gray-500">보유일</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">손익금액</th>
                          <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">손익률</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tradeHistory.map((trade) => (
                          <tr key={trade.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2 px-2 text-xs text-gray-600 tabular-nums">{trade.closed_date}</td>
                            <td className="py-2 px-2 text-xs font-medium text-gray-800">{trade.stock_name}</td>
                            <td className="py-2 px-2 text-xs text-right text-gray-600 tabular-nums">{trade.buy_price?.toLocaleString()}</td>
                            <td className="py-2 px-2 text-xs text-gray-600 tabular-nums">{trade.buy_date}</td>
                            <td className="py-2 px-2 text-xs text-right text-gray-600 tabular-nums">{trade.sell_price?.toLocaleString()}</td>
                            <td className="py-2 px-2 text-xs text-center text-gray-600">{trade.holding_days_display}</td>
                            <td className={`py-2 px-2 text-xs text-right font-medium tabular-nums ${
                              trade.profit_loss >= 0 ? 'text-red-500' : 'text-blue-500'
                            }`}>
                              {trade.profit_loss >= 0 ? '+' : ''}{trade.profit_loss?.toLocaleString()}원
                            </td>
                            <td className={`py-2 px-2 text-xs text-right font-medium tabular-nums ${
                              trade.profit_rate >= 0 ? 'text-red-500' : 'text-blue-500'
                            }`}>
                              {trade.profit_rate >= 0 ? '+' : ''}{trade.profit_rate?.toFixed(2)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400 text-xs">
                    청산된 거래내역이 없습니다
                  </div>
                )}
              </div>
            )}

            {/* 보유종목 차트 탭 */}
            {activeTab === 'holdingsChart' && (
              <div className="space-y-3">
                <h3 className="text-sm text-gray-500 font-medium">보유종목 차트</h3>
                {holdingsChartLoading ? (
                  <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400 text-xs">
                    차트 로딩 중...
                  </div>
                ) : holdingsChartData.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {holdingsChartData.map((holding) => (
                      <StockChartCard key={holding.stock_code} holding={holding} />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400 text-xs">
                    보유 중인 종목이 없습니다
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </main>
    </div>
    </CollectingOverlay>
  );
}

export default Dashboard;
